import React, { useEffect, useLayoutEffect, useRef, useState, useMemo, useCallback } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

// Styles
import '../styles/SideBar.css'

// Services
import { getNumberProfilesForReview, getUnseenConversations, getById } from '../server/server'

// Contexts and Hooks
import { useSocket } from '../context/socket'
import { useUser } from '../auth'
import { useInstitution } from '../auth/InstitutionContext'
import { useAppContext } from '../context/AppContext'
import { isItAccess } from '../constants/Roles'

// Components
import LogoutModal from './modals/LogoutModal'
import SidebarItem from './SidebarItem'
import LanguageSelector from './LanguageSelector'
import SidebarLogo from './SidebarLogo'

// Utils and Constants
import i18n from '../i18n'
import { getLanguagePack, prefersReducedMotion } from '../utils/util'
import { sidebarDataPrimary, sidebarDataRegular } from '../constants/Constants'
import { SIDEBAR_STATE, SIDEBAR_COLLAPSED_KEY, SIDEBAR_TRANSITION_FALLBACK } from '../constants/sidebar'
import useSidebarState from '../hooks/useSidebarState'

// SVG Icons
import { ReactComponent as Dashboard } from '../assets/svg/dashboard.svg'
import { ReactComponent as Content } from '../assets/svg/content.svg'
import { ReactComponent as Classroom } from '../assets/svg/classroom.svg'
import { ReactComponent as Analyze } from '../assets/svg/analyze.svg'
import { ReactComponent as Chat } from '../assets/svg/chat.svg'
import { ReactComponent as Management } from '../assets/svg/management.svg'
import { ReactComponent as Wellbeing } from '../assets/svg/wellbeing.svg'
import { ReactComponent as Videos } from '../assets/svg/videos.svg'
import { ReactComponent as Settings } from '../assets/svg/settings.svg'
import { ReactComponent as Logout } from '../assets/svg/logout.svg'
import { ReactComponent as CatchUp } from '../assets/svg/catch-up.svg'
import { ReactComponent as Alerts } from '../assets/svg/alerts.svg'
import { ReactComponent as NavPin } from '../assets/svg/nav-pin.svg'

// Icon mapping for better maintainability
const ICON_COMPONENTS = {
    dashboard: Dashboard,
    content: Content,
    classroom: Classroom,
    analyze: Analyze,
    chat: Chat,
    management: Management,
    wellbeing: Wellbeing,
    videos: Videos,
    settings: Settings,
    logout: Logout,
    'catch-up': CatchUp,
    alerts: Alerts,
}

// Full-bleed child-facing screens. The rail is position: fixed and overlays their content,
// and the teacher chrome is both a distraction and a mis-tap risk for a 4-8 year old, so it
// rests collapsed there and the pin is hidden. Exact match, so /early/classroom/student-settings
// (a teacher screen sitting under the same prefix) is deliberately NOT included.
// Query strings live outside location.pathname, so /early/classroom?id=123 matches here.
const CHROME_FREE_PATHS = ['/early/classroom', '/early/classroom/student-select-emotion']

const clearSlide = (element) => {
    element.style.transition = ''
    element.style.transform = ''
}

// FLIP "invert + play": start `element` visually `offset` px from where layout just put
// it, then transition the transform away with the sidebar's own timing. Returns a
// cleanup that detaches listeners but leaves the styles, so a re-run can measure the
// in-flight position; the inline styles are dropped once the slide lands, because a
// lingering transform would make the page the containing block for position: fixed.
const slideFrom = (element, offset) => {
    element.style.transition = 'none'
    element.style.transform = `translateX(${offset}px)`
    // Commit the start position before transitioning away from it.
    void element.offsetWidth
    element.style.transition = 'transform var(--sb-dur-open) var(--sb-ease-open)'
    element.style.transform = ''

    const finish = () => clearSlide(element)
    const handleEnd = (event) => {
        if (event.target === element && event.propertyName === 'transform') finish()
    }
    const fallback = setTimeout(finish, SIDEBAR_TRANSITION_FALLBACK)
    element.addEventListener('transitionend', handleEnd)

    return () => {
        clearTimeout(fallback)
        element.removeEventListener('transitionend', handleEnd)
    }
}

// The bottom group fades in last, at the stagger cap (see .sb-reveal in SideBar.css).
const BOTTOM_LABEL_STYLE = { '--sb-i': 10 }

const isChromeFreeRoute = (pathname = '') => CHROME_FREE_PATHS.includes(pathname.replace(/\/+$/, '') || '/')

function SideBar() {
    // Router hooks
    const location = useLocation()
    const history = useHistory()

    // Context hooks
    const { user, setUser } = useUser()
    const { appState, setAppState } = useAppContext()
    const { institution, setInstitution } = useInstitution()
    const { socketStates, changeSocketState } = useSocket()
    const { t } = useTranslation()

    // Local state
    const [homeStatus, setHomeStatus] = useState('/dashboard')
    const [selectedLang, setSelectedLanguage] = useState(i18n.language)
    // The route override is never persisted: the teacher's stored preference is left untouched
    // and applies again by itself the moment they navigate off these screens.
    const chromeFree = isChromeFreeRoute(location.pathname)
    const { rootRef, state, pinnedPref, showPin, togglePin, consumeUserToggle, handlers } = useSidebarState({ forceUnpinned: chromeFree })
    const collapsed = state === SIDEBAR_STATE.COLLAPSED
    const restCollapsed = state !== SIDEBAR_STATE.PINNED
    // Skip transitions for the first frame so a persisted state paints in place.
    const [firstPaintDone, setFirstPaintDone] = useState(false)
    const [logoutModalOpen, setLogoutModalOpen] = useState(false)
    const [showContent, setShowContent] = useState(false)
    const [showAlerts, setShowAlerts] = useState(false)

    // Computed values
    const numberOfUnseenConversations = socketStates?.unseenConversations?.length || 0
    const primaryUI = !!appState?.primary
    const isAdmin = user?.role === 'school_admin' || user?.role_coverage?.target_text === 'All students'
    const langs = useMemo(() => getLanguagePack(user?.institution_id), [user?.institution_id])
    const uiModeLabel = appState.primary ? t('Open Upstrive Education') : t('Open Upstrive Early')

    // Sidebar data based on UI mode
    const sidebarData = useMemo(
        () => (primaryUI ? sidebarDataPrimary() : sidebarDataRegular(numberOfUnseenConversations, isAdmin)),
        [primaryUI, numberOfUnseenConversations, isAdmin]
    )

    // Effects
    useEffect(() => {
        const pathList = location.pathname.split('/').slice(1)
        const shortenedPath = '/' + pathList.join('/')
        setHomeStatus(shortenedPath)
    }, [location.pathname])

    // API calls
    const loadUnseenConversations = async () => {
        try {
            const response = await getUnseenConversations()
            if (response.status === 200) {
                changeSocketState({ unseenConversations: response?.data || [] })
            }
        } catch (error) {
            console.error('Failed to load unseen conversations:', error)
        }
    }

    const loadProfilesForReview = async () => {
        try {
            const profiles = await getNumberProfilesForReview()
            if (profiles.status === 200) {
                // Store profiles if needed for future use
                console.log('Profiles loaded:', profiles.data)
            }
        } catch (error) {
            console.error('Failed to load profiles for review:', error)
        }
    }

    const loadInstitution = async () => {
        try {
            const resp = await getById({ collection: 'institutions', id: user.institution_id })
            if (resp.status === 200) {
                setInstitution(resp?.data)
            }
        } catch (error) {
            console.error('Failed to load institution:', error)
        }
    }

    // Initialize data on mount
    useEffect(() => {
        // IT Access has no chat or wellbeing-review features; skip those calls
        // so the sidebar doesn't fire forbidden (403) requests on load.
        if (!isItAccess(user)) {
            loadUnseenConversations()
            loadProfilesForReview()
        }
        loadInstitution()
    }, [])

    // Update document title based on unseen conversations
    useEffect(() => {
        const unseenCount = socketStates?.unseenConversations?.length
        const currentTitle = document.title
        const baseTitleMatch = currentTitle.match(/\)\s*(.+)$/)
        const baseTitle = baseTitleMatch ? baseTitleMatch[1] : currentTitle

        document.title = unseenCount ? `(${unseenCount}) ${baseTitle}` : baseTitle
    }, [socketStates?.unseenConversations?.length])

    useEffect(() => {
        let second
        const first = requestAnimationFrame(() => {
            second = requestAnimationFrame(() => setFirstPaintDone(true))
        })
        return () => {
            cancelAnimationFrame(first)
            cancelAnimationFrame(second)
        }
    }, [])

    // Promote the panel to its own layer only while it sweeps. A permanent will-change
    // would hold two full-height layers in memory for the whole session.
    const previousState = useRef(state)
    useLayoutEffect(() => {
        const wasCollapsed = previousState.current === SIDEBAR_STATE.COLLAPSED
        previousState.current = state
        const root = rootRef.current
        if (!root || !firstPaintDone) return undefined

        // peek <-> pinned does not move the panel; only a sweep needs a layer, but one
        // that is still running from a moment ago must still get cleaned up.
        const sweeping = wasCollapsed !== collapsed
        if (!sweeping && root.dataset.animating === undefined) return undefined

        if (sweeping) root.dataset.animating = ''
        const finish = () => delete root.dataset.animating
        const handleEnd = (event) => {
            if (event.target === root && event.propertyName === 'transform') finish()
        }
        // Not transitioncancel: a reversal cancels the running transition right after this
        // listener is attached, while the replacement sweep still needs the layer. The
        // timeout covers transitions that never run (reduced motion, hidden tab).
        const fallback = setTimeout(finish, SIDEBAR_TRANSITION_FALLBACK)
        root.addEventListener('transitionend', handleEnd)

        // A reversal re-runs this effect; the attribute stays set so the layer is kept.
        return () => {
            clearTimeout(fallback)
            root.removeEventListener('transitionend', handleEnd)
        }
    }, [state, collapsed, firstPaintDone, rootRef])

    // Nested menus only make sense while labels are visible; close them with the panel.
    useEffect(() => {
        if (collapsed) {
            setShowContent(false)
            setShowAlerts(false)
        }
    }, [collapsed])

    // Reserve the pinned panel's width in the page. useLayoutEffect, not useEffect: the
    // static `.home-container` rule reserves only the rail, so deferring this would paint
    // the narrow margin first and jump on every load now that pinned is the default.
    //
    // The margin itself is never transitioned (that re-laid out the dashboard on every
    // frame). The page re-lays out once, and a deliberate pin/unpin then slides it from
    // where it was to where it now is using a transform (FLIP).
    useLayoutEffect(() => {
        const homeContainer = document.getElementById('home-container')
        const rootElement = document.getElementById('root')
        const animate = consumeUserToggle() && !prefersReducedMotion()
        // Includes any in-flight slide, so re-toggling mid-slide continues from where it is.
        const first = animate && homeContainer ? homeContainer.getBoundingClientRect().left : 0

        if (homeContainer) homeContainer.style.marginLeft = restCollapsed ? 'var(--sb-rail)' : 'var(--sb-expanded)'
        // Lets the responsive widget compensations follow the rail.
        if (rootElement) rootElement.classList.toggle('sidebarPinned', !restCollapsed)

        if (!animate || !homeContainer) return undefined
        // "Last" is the new resting position, so drop any in-flight slide before measuring.
        homeContainer.style.transition = 'none'
        homeContainer.style.transform = 'none'
        const offset = first - homeContainer.getBoundingClientRect().left
        if (!offset) {
            clearSlide(homeContainer)
            return undefined
        }
        return slideFrom(homeContainer, offset)
    }, [restCollapsed, consumeUserToggle])

    // Never leave the page translated if the sidebar unmounts mid-slide (logout).
    useEffect(
        () => () => {
            const homeContainer = document.getElementById('home-container')
            if (homeContainer) clearSlide(homeContainer)
        },
        []
    )

    // Update selected language when i18n language changes
    useEffect(() => {
        setSelectedLanguage(i18n.language)
    }, [appState.locale])

    // Render icon component based on name
    const renderIcon = (name, color) => {
        const IconComponent = ICON_COMPONENTS[name] || Dashboard
        // The surrounding link/button carries the accessible name, so the glyph is decorative.
        const commonProps = { fill: color, 'aria-hidden': true, focusable: false }

        // Special case for alerts (uses stroke instead of fill)
        if (name === 'alerts') {
            return <IconComponent stroke={color} aria-hidden="true" focusable="false" />
        }

        // Special case for dashboard (has specific dimensions)
        if (name === 'dashboard') {
            return <IconComponent {...commonProps} height={17} width={16} />
        }

        return <IconComponent {...commonProps} />
    }

    // Handle language change
    const handleLangChange = (code) => {
        setSelectedLanguage(code)
        i18n.changeLanguage(code)
        localStorage.setItem('locale', code)
        setAppState((prev) => ({ ...prev, locale: code }))
    }

    // Modal handlers
    const openLogoutModal = useCallback(() => setLogoutModalOpen(true), [])
    const closeModal = useCallback(() => setLogoutModalOpen(false), [])

    // Logout handler
    const logout = () => {
        // The sidebar preference is a device setting, not session data — carry it across
        // the clear() so a teacher's pinned-closed rail survives signing out and back in.
        let storedCollapsed = null
        try {
            storedCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
        } catch (error) {
            storedCollapsed = null
        }

        localStorage.clear()

        if (storedCollapsed !== null) {
            try {
                localStorage.setItem(SIDEBAR_COLLAPSED_KEY, storedCollapsed)
            } catch (error) {
                console.error('Failed to persist sidebar state:', error)
            }
        }

        setUser({})
        setAppState({})
        history.push('/')
        setLogoutModalOpen(false)
    }

    // UI mode toggle handler
    const handleUIModeToggle = () => {
        const nextState = !appState?.primary
        setAppState({ ...appState, primary: nextState })
        localStorage.setItem('uiMode', nextState ? 'primary' : 'regular')
        history.push('/dashboard')
    }

    // Dropdown toggle handlers
    // One updater per menu: toggle the clicked one, close the other. (Queuing a reset to
    // false before `prev => !prev` batched into `!false`, so a second click never closed.)
    const handleDropdownToggle = useCallback((item) => {
        const isContent = item.title === 'content'
        const isAlerts = item.title === 'Alerts'
        setShowContent((prev) => (isContent ? !prev : false))
        setShowAlerts((prev) => (isAlerts ? !prev : false))
    }, [])

    // Check if dropdown is open
    const isDropdownOpen = useCallback(
        (item) => {
            if (item.title === 'content' && showContent) return true
            if (item.title === 'Alerts' && showAlerts) return true
            return false
        },
        [showContent, showAlerts]
    )

    // The panel is always laid out at full width and revealed with transforms (see
    // styles/SideBar.css), so nothing inside it re-wraps mid-sweep. The pin is a sibling
    // of the clipping layer because it hangs off the panel's trailing edge.
    return (
        <>
            <div id="sideBar" ref={rootRef} data-state={state} className={`sb ${firstPaintDone ? '' : 'sb--no-transition'}`} {...handlers}>
                {showPin && (
                    <button
                        type="button"
                        className="nav-pin bg-deepPurple"
                        onClick={togglePin}
                        aria-pressed={!restCollapsed}
                        aria-controls="sideBar"
                        aria-label={pinnedPref ? t('collapseSidebar') : t('expandSidebar')}
                        title={pinnedPref ? t('collapseSidebar') : t('expandSidebar')}
                    >
                        {/* Yellow means the rail is held open. Driving the fill off the effective
                            state rather than the stored preference keeps the pin truthful if anything
                            other than the preference forces the rail closed. */}
                        <NavPin fill={restCollapsed ? '#F8F8FB' : '#FDDF6D'} style={{ display: 'block' }} aria-hidden="true" focusable="false" />
                    </button>
                )}
                <div className="sb__clip bg-deepPurple">
                    <div className="sb__inner flex flex-col items-center">
                        <SidebarLogo primaryUI={primaryUI} />
                        <div className="my-[5px] min-h-6 min-w-6">
                            <span className="sb-reveal block px-[30px] text-[#F8F8FB] text-[16px] font-bold w-full ellipsis max-w-[270px] text-ellipsis">{institution.name}</span>
                        </div>
                        <LanguageSelector langs={langs} selectedLang={selectedLang} onLanguageChange={handleLangChange} />
                        <div className="mt-1 xl3:mt-6 w-full flex flex-col sb-nav overflow-y-auto overflow-x-hidden">
                            <nav aria-label={t('mainNavigation')} className=" text-[#F8F8FB] font-bold text-lg flex flex-col justify-between h-[100vh]">
                                <div className="link-list">
                                    {sidebarData?.map((item, index) => {
                                        if (item.name === 'videos' && i18n.language !== 'en') return null
                                        if (user.hasOnlyPrimaryStudents && ['content', 'chat'].includes(item.name)) return null
                                        // IT Access is restricted to User Management; hide every other nav item.
                                        if (isItAccess(user) && item.name !== 'management') return null

                                        return (
                                            <SidebarItem
                                                key={item?.path[0]}
                                                item={item}
                                                homeStatus={homeStatus}
                                                index={index}
                                                renderIcon={renderIcon}
                                                setHomeStatus={setHomeStatus}
                                                isOpen={isDropdownOpen(item)}
                                                onToggle={handleDropdownToggle}
                                            />
                                        )
                                    })}
                                </div>
                                <div className="bottom-container">
                                    {!!user?.hasPrimaryClasses && !user.hasOnlyPrimaryStudents && (
                                        <div id={`sidebar-navigation-button-upstrive-early`} className=" nav-text cursor-pointer">
                                            <button
                                                type="button"
                                                className={`h-[45px] 2xl:h-[52px] flex flex-row  items-center relative sb-row transition-[height] w-full text-start`}
                                                onClick={handleUIModeToggle}
                                                aria-label={uiModeLabel}
                                            >
                                                <div className="sb-icon">{renderIcon('switch', '#F8F8FB')}</div>
                                                <span className="sb-reveal sb-label px-3 w-full ellipsis text-ellipsis" style={BOTTOM_LABEL_STYLE}>
                                                    {uiModeLabel}
                                                </span>
                                            </button>
                                        </div>
                                    )}
                                    <div
                                        className=" cursor-pointer nav-text"
                                        onClick={() => {
                                            setHomeStatus('/settings')
                                        }}
                                    >
                                        <Link to="/settings" aria-label={t('settings')}>
                                            <div id="sidebar-navigation-button-settings" className={`h-[52px] flex flex-row  items-center relative sb-row w-full cursor-pointer`}>
                                                <div className="sb-icon">{renderIcon('settings', homeStatus === '/settings' ? '#FDDF6D' : '#F8F8FB')}</div>
                                                <span className={`sb-reveal sb-label px-3 ${homeStatus === '/settings' ? ' text-highlightColor' : ''}`} style={BOTTOM_LABEL_STYLE}>
                                                    {t('settings')}
                                                </span>
                                            </div>
                                        </Link>
                                    </div>
                                    <div className=" nav-text cursor-pointer">
                                        <button
                                            type="button"
                                            className={`h-[52px] flex flex-row  items-center relative sb-row w-full text-start`}
                                            onClick={() => openLogoutModal()}
                                            aria-label={t('logout')}
                                        >
                                            <div id="logout-button" className="sb-icon">
                                                {renderIcon('logout', '#F8F8FB')}
                                            </div>
                                            <span className="sb-reveal sb-label px-3 w-full ellipsis text-ellipsis" style={BOTTOM_LABEL_STYLE}>
                                                {t('logout')}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            {/* Outside the panel's React tree: portal events would otherwise bubble into the
                sidebar's hover/focus/Escape handlers. */}
            <LogoutModal modalIsOpen={logoutModalOpen} closeModal={closeModal} logout={logout} />
        </>
    )
}

export default SideBar
